import {
  WSActions,
  WSAlert,
  WSCard,
  WSDivider,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { getCurrentTaxPaymentQuarter } from "@wingspanhq/utils/dist/date/taxDeadlines";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/users";
import React from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccountBalance } from "../../../query/banking/queries/useQueryInternalAccountBalance";
import { PATH_TAXES_SCHEDULE_PAYMENT } from "../paths";
import { NextPaymentInfo } from "./NextPaymentInfo";
import { useGoToTransfer } from "../../Transfer/paths";
import { useModalCancelPendingWithholding } from "../modals/ModalCancelPendingWithholding";
import { useTaxPaymentScheduling } from "../hooks/useTaxPaymentScheduling";
import { selectorIsScheduledTaxPaymentCancellable } from "../selectors/selectorIsScheduledTaxPaymentCancellable";
import { formatInternalDeadline } from "../utils";

type Props = {
  internalAccountId?: string;
};

export const AvailableBalance: React.FC<Props> = props => {
  const history = useHistory();
  const goToTransfer = useGoToTransfer();
  const modalCancelPendingWithholding = useModalCancelPendingWithholding();

  const queryBalance = useQueryInternalAccountBalance(
    props.internalAccountId!,
    {
      enabled: !!props.internalAccountId
    }
  );

  const {
    scheduledPayments,
    scheduledPaymentAmount,
    processingPaymentAmount,
    hasScheduledPayment,
    hasProcessingPayment,
    hasScheduledTaxPayment,
    hasProcessingTaxPayment,
    isLoading: isTaxPaymentSchedulingLoading
  } = useTaxPaymentScheduling();

  const availableBalance = queryBalance.data?.available || 0;
  const currentBalance = queryBalance.data?.balance || 0;
  const currency = queryBalance.data?.currency || "USD";
  const scheduledTaxPayment = scheduledPayments[0];
  const isScheduledTaxPaymentCancellable = scheduledTaxPayment
    ? selectorIsScheduledTaxPaymentCancellable({
        quarter: scheduledTaxPayment.labels?.quarter,
        year: scheduledTaxPayment.labels?.year
      })
    : false;
  const shouldShowCancelScheduledPayment =
    !hasProcessingTaxPayment &&
    hasScheduledTaxPayment &&
    isScheduledTaxPaymentCancellable;
  const currentQuarter = getCurrentTaxPaymentQuarter();
  const hasScheduledTaxPaymentForCurrentQuarter = scheduledPayments.some(
    payment =>
      payment.labels.quarter === currentQuarter.quarter &&
      Number(payment.labels.year) === currentQuarter.year
  );

  if (!props.internalAccountId) {
    return (
      <WSCard header="Available balance">
        <WSList gap="XL">
          <WSText.Heading1 weight="book" color="gray300">
            $--
          </WSText.Heading1>
          <NextPaymentInfo />
        </WSList>
      </WSCard>
    );
  }

  return (
    <WSCard header="Available balance" shimmer={queryBalance.isInitialLoading}>
      <WSList gap="XL">
        <WSText.Heading1 weight="book" color="gray500">
          {formatMoney(availableBalance, currency)}
        </WSText.Heading1>

        {currentBalance ? (
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm color="gray500">
              Total balance
            </WSText.ParagraphSm>
            <WSText.ParagraphSm color="gray600">
              {formatMoney(currentBalance, currency)}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        ) : null}

        <WSDivider />

        {hasScheduledPayment ? (
          <>
            <WSText.Paragraph weight="medium">Upcoming</WSText.Paragraph>

            <WSFlexBox.CenterY justify="space-between">
              <WSFlexBox.CenterY gap="XS">
                <WSText.ParagraphSm color="gray500">
                  Scheduled payment
                </WSText.ParagraphSm>
                <WSIcon name="info-circle-fill" size="XS" color="gray500" />
              </WSFlexBox.CenterY>
              <WSText.ParagraphSm color="gray600">
                {formatMoney(scheduledPaymentAmount, currency)}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
          </>
        ) : null}

        {hasProcessingPayment ? (
          <>
            <WSText.Paragraph weight="medium">Processing now</WSText.Paragraph>

            <WSFlexBox.CenterY justify="space-between">
              <WSFlexBox.CenterY gap="XS">
                <WSText.ParagraphSm color="gray500">
                  Pending amounts
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>
              <WSText.ParagraphSm color="gray600">
                {formatMoney(processingPaymentAmount, currency)}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
            {hasProcessingTaxPayment ? (
              <WSAlert
                theme="neutral"
                title="Payment processing"
                icon="info-circle"
              >
                No changes or cancellations to scheduled payments can be made
                for this quarter to ensure on-time processing and avoid late
                penalties.
              </WSAlert>
            ) : null}
          </>
        ) : null}

        <WSActions
          alignment="fill"
          buttons={[
            ...(hasProcessingTaxPayment
              ? [
                  {
                    label: "Edit scheduled payment",
                    kind: "Secondary" as const,
                    disabled: true
                  }
                ]
              : []),
            ...(shouldShowCancelScheduledPayment
              ? [
                  {
                    label: "Cancel scheduled payment",
                    kind: "Secondary" as const,
                    destructive: true,
                    onClick: () => {
                      modalCancelPendingWithholding.open({
                        pendingWithholdingId: scheduledTaxPayment.transactionId,
                        amount: scheduledTaxPayment.amount,
                        currency: scheduledTaxPayment.currency as Currency,
                        date: scheduledTaxPayment.createdAt
                      });
                    }
                  }
                ]
              : []),
            ...(!hasProcessingTaxPayment && !hasScheduledTaxPayment
              ? [
                  {
                    label: "Schedule tax payment",
                    kind: "Secondary" as const,
                    loading: isTaxPaymentSchedulingLoading,
                    onClick: () => {
                      history.push(PATH_TAXES_SCHEDULE_PAYMENT);
                    }
                  }
                ]
              : []),
            {
              label: "Transfer",
              kind: "Primary",
              onClick: () => {
                goToTransfer({
                  balance: "taxWithholdings"
                });
              }
            }
          ]}
        />

        {hasScheduledTaxPaymentForCurrentQuarter && !hasProcessingTaxPayment ? (
          <WSAlert
            theme="neutral"
            icon="info-circle"
            title={`Your payment is scheduled for ${currentQuarter.quarter} ${currentQuarter.year}`}
          >
            Need to make changes? You can cancel and reschedule anytime before
            this quarter&apos;s processing deadline{" "}
            <b>{formatInternalDeadline(currentQuarter.internalDeadline)}</b>.
            After this date, your payment will be locked for processing and
            cannot be cancelled.
          </WSAlert>
        ) : null}

        <NextPaymentInfo />
      </WSList>
    </WSCard>
  );
};
